// Оформление: режим × стиль × палитра — ровно как в teen-english
// (src/theme/appearance.ts там). Решение владельца от 28 октября 2026: стили,
// цвета, шрифты и раскладка оттуда, включая повёрнутые карточки и наклейки.
//
// Три независимых выбора, и у дня и ночи свои палитры: переключение режима
// никогда не переиспользует дневные цвета ночью. Значения токенов —
// src/appearance.css; здесь только выбор и атрибуты на <html>.
//
// **`data-theme` ставится тоже, равным режиму.** Его читают черновик, доска,
// обратная связь и тёмные блоки tutor-atlas.css и function-lab.css. Переводить
// их на `data-mode` — правка в пяти чужих местах ради имени; атрибут дешевле.
//
// **«Как в системе» больше нет** как отдельного состояния: в teen-english его
// нет, и панель там на два положения. Система решает только первый вход, пока
// ничего не выбрано.

import { useSyncExternalStore } from "react";

export type Mode = "light" | "dark";
export type Look = "strict" | "soft";
export type LightPalette = "case" | "archive";
export type DarkPalette = "night" | "ocean";
export type Appearance = { mode: Mode; look: Look; palLight: LightPalette; palDark: DarkPalette };

export const LIGHT_PALETTES: { id: LightPalette; label: string; colors: string[] }[] = [
  { id: "case", label: "Дело", colors: ["#c2362b", "#2e5f8f", "#f2d59c"] },
  { id: "archive", label: "Архив", colors: ["#1d5c63", "#b5542f", "#e9dcc0"] },
];
export const DARK_PALETTES: { id: DarkPalette; label: string; colors: string[] }[] = [
  { id: "night", label: "Ночь", colors: ["#c4ff3d", "#9d8cff", "#ff5ea8"] },
  { id: "ocean", label: "Океан", colors: ["#2de0c8", "#fbbf24", "#38bdf8"] },
];

// Дублируется в inline-скрипте index.html, который ставит атрибуты до первой
// отрисовки. Меняя здесь — менять там.
const KEY = "traektoria.appearance";
// Ключ прежнего переключателя на три положения. Читается один раз, чтобы
// выбор «тёмная» пережил переезд.
const LEGACY_KEY = "traektoria.theme";
// `?theme=dark` — посмотреть ночь на дневной машине; в адресе не остаётся.
const PARAM = "theme";

const THEME_COLOR: Record<string, string> = {
  "light-strict": "#e8dfcc",
  "light-soft": "#f0eeea",
  "dark-strict": "#16140f",
  "dark-soft": "#0a0b12",
};

const prefersDark = () => window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;

export function defaultAppearance(dark = false): Appearance {
  return { mode: dark ? "dark" : "light", look: "strict", palLight: "case", palDark: "night" };
}

export function parseAppearance(raw: unknown, fallback: Appearance): Appearance {
  const v = (raw && typeof raw === "object" ? raw : {}) as Partial<Appearance>;
  return {
    mode: v.mode === "dark" || v.mode === "light" ? v.mode : fallback.mode,
    look: v.look === "soft" || v.look === "strict" ? v.look : fallback.look,
    palLight: LIGHT_PALETTES.some((p) => p.id === v.palLight) ? (v.palLight as LightPalette) : fallback.palLight,
    palDark: DARK_PALETTES.some((p) => p.id === v.palDark) ? (v.palDark as DarkPalette) : fallback.palDark,
  };
}

export const paletteOf = (a: Appearance) => (a.mode === "light" ? a.palLight : a.palDark);

function dropParam(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(PARAM);
    window.history.replaceState(null, "", url.toString());
  } catch {
    // Адрес не переписался — выбор всё равно применён.
  }
}

function load(): Appearance {
  let fallback = defaultAppearance(prefersDark());
  try {
    const legacy = window.localStorage.getItem(LEGACY_KEY);
    if (legacy === "light" || legacy === "dark") fallback = { ...fallback, mode: legacy };
  } catch {
    // приватное окно
  }
  let a = fallback;
  try {
    a = parseAppearance(JSON.parse(window.localStorage.getItem(KEY) || "null"), fallback);
  } catch {
    // испорченная запись — берём запасной вариант
  }
  const fromLink = new URLSearchParams(window.location.search).get(PARAM);
  if (fromLink === "light" || fromLink === "dark") {
    a = { ...a, mode: fromLink };
    store(a);
    dropParam();
  }
  return a;
}

function store(a: Appearance): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(a));
  } catch {
    // приватное окно — выбор живёт до перезагрузки
  }
}

let state: Appearance = typeof window === "undefined" ? defaultAppearance() : load();
const listeners = new Set<() => void>();

function apply(a: Appearance): void {
  const root = document.documentElement;
  root.dataset.mode = a.mode;
  root.dataset.look = a.look;
  root.dataset.palette = paletteOf(a);
  root.dataset.theme = a.mode;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[`${a.mode}-${a.look}`]);
}

export function setAppearance(patch: Partial<Appearance>): void {
  state = { ...state, ...patch };
  store(state);
  apply(state);
  listeners.forEach((l) => l());
}

export const getAppearance = () => state;

export const useAppearance = () =>
  useSyncExternalStore((l) => {
    listeners.add(l);
    return () => listeners.delete(l);
  }, getAppearance);

/**
 * Применить сохранённый выбор. Скрипт в index.html уже поставил атрибуты —
 * здесь они ставятся второй раз ради `?theme=`, о котором скрипт не знает.
 */
export function initTheme(): void {
  apply(state);
}
